/**
 * IdGenerator.ts - Centralized ID Generation Utility
 * 
 * CHANGE HISTORY:
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
let idCounter = 0;

/**
 * Generate a numeric ID (default)
 * Format: timestamp (13 digits) + counter (4 digits) + random (3 digits)
 * Example: 1729012345678-0001-123
 * 
 * This ensures:
 * - Uniqueness across sessions (timestamp)
 * - Uniqueness within session (counter)
 * - Additional entropy (random)
 * - Sortable by creation time
 * - Numeric-friendly (can be parsed as BigInt if needed)
 */
export const generateId = () => {
  const timestamp = Date.now();
  idCounter = (idCounter + 1) % 10000; // Reset after 9999
  const counter = String(idCounter).padStart(4, '0');
  const random = String(Math.floor(Math.random() * 1000)).padStart(3, '0');
  return `${timestamp}${counter}${random}`;
};

/**
 * Generate a text UUID (RFC 4122 v4)
 * Use this only when compatibility with external systems requires it
 * Example: "550e8400-e29b-41d4-a716-446655440000"
 */
export const generateUUID = () => {
  // Use crypto.randomUUID if available (modern browsers)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  // Fallback to manual generation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Generate a short numeric ID (for UI display)
 * Format: 8-digit number
 * Example: 12345678
 * 
 * WARNING: Not guaranteed to be globally unique
 * Use only for temporary IDs or UI display
 */
export const generateShortId = () => {
  return String(Math.floor(Math.random() * 100000000)).padStart(8, '0');
};

/**
 * Validate if a string is a valid numeric ID
 */
export const isNumericId = (id) => {
  if (typeof id !== 'string') return false;
  return /^\d{13}\d{4}\d{3}$/.test(id);
};

/**
 * Validate if a string is a valid UUID
 */
export const isUUID = (id) => {
  if (typeof id !== 'string') return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id);
};

/**
 * Extract timestamp from numeric ID
 */
export const getTimestampFromId = (id) => {
  if (!isNumericId(id)) return null;
  return parseInt(id.substring(0, 13), 10);
};

/**
 * Compare two IDs chronologically
 * Returns: -1 if id1 < id2, 0 if equal, 1 if id1 > id2
 */
export const compareIds = (id1, id2) => {
  if (isNumericId(id1) && isNumericId(id2)) {
    const t1 = getTimestampFromId(id1);
    const t2 = getTimestampFromId(id2);
    return t1 < t2 ? -1 : t1 > t2 ? 1 : 0;
  }
  // Fallback to string comparison
  return id1 < id2 ? -1 : id1 > id2 ? 1 : 0;
};

/**
 * Configuration for ID generation
 * Can be set by applications to customize behavior
 */
export const IdConfig = {
  // Default ID type: 'numeric' or 'uuid'
  defaultType: 'numeric',
  
  // Prefix for IDs (optional)
  prefix: '',
  
  // Use UUID for specific contexts (e.g., FileMaker sync)
  useUUIDFor: ['filemaker', 'external'],
};

/**
 * Generate ID based on context
 * @param {string} context - Context for ID generation (e.g., 'node', 'filemaker', 'dataset')
 */
export const generateIdForContext = (context = 'default') => {
  // Use UUID for specific contexts
  if (IdConfig.useUUIDFor.includes(context)) {
    return IdConfig.prefix + generateUUID();
  }
  
  // Use configured default type
  if (IdConfig.defaultType === 'uuid') {
    return IdConfig.prefix + generateUUID();
  }
  
  // Default to numeric ID
  return IdConfig.prefix + generateId();
};

export default {
  generateId,
  generateUUID,
  generateShortId,
  generateIdForContext,
  isNumericId,
  isUUID,
  getTimestampFromId,
  compareIds,
  IdConfig
};
